import { SandboxVisibilityUI } from "components/ShareButton/ShareSandbox/components/SandboxVisibility";
import { useSandboxEditor } from "features/editor/Feature";
import type { VisibilityNumber } from "features/editor/sandboxUtils";
import {
  numberToVisibility,
  visibilityToNumber,
} from "features/editor/sandboxUtils";
import { useForkSandbox } from "features/forkSandbox";
import { useCurrentUser } from "hooks/api/useCurrentUser";
import { useSession } from "hooks/api/useSession";
import {
  Box,
  Button,
  Stack,
  Dialog,
  Select,
  Text,
  Icon,
  Avatar,
  Badge,
} from "prism-react";
import { useState } from "react";

import { GlobalModal } from "components/GlobalModal";
import { useActiveWorkspace } from "hooks/useActiveWorkspace";
import { useUserWorkspaces } from "hooks/useUserWorkspaces";
import { paths } from "utils/router";
import { getIsPro, getIsTeamAdmin } from "utils/subscription";
import { isUrl } from "utils/url";

export const ForkErrorModal: React.FC<{
  onDismiss: () => void;
  message: string;
  workspaceIdFromFork: string | null;
}> = ({ message, workspaceIdFromFork, onDismiss }) => {
  const [, sessionApi] = useSession();

  const isSignInError = message.includes("You need to be signed in");
  const messageParts = message;

  if (message.includes("SANDBOX_LIMIT")) {
    return (
      <SandboxForkLimit
        onDismiss={onDismiss}
        workspaceIdFromFork={workspaceIdFromFork}
      />
    );
  }

  if (message.includes("has already been taken")) {
    message =
      "This branch name has already been taken. Please try to create a branch again with a different name, or click on Create branch once again.";
  }

  return (
    <GlobalModal
      maxWidth={500}
      onOpenChange={() => {
        onDismiss();
      }}
      open
    >
      <Box
        css={{
          position: "absolute",
          right: "$4",
          top: "$4",
          zIndex: 200,
        }}
      >
        <Dialog.CloseIconButton />
      </Box>

      <Dialog.Title>Forking Failed</Dialog.Title>

      <Dialog.Description>
        {messageParts.split(" ").map((part) => {
          if (isUrl(part)) {
            return (
              <>
                <a href={part}>{part}</a>{" "}
              </>
            );
          }

          return (
            <>
              <span>{part}</span>{" "}
            </>
          );
        })}
      </Dialog.Description>

      <Stack css={{ justifyContent: "flex-end" }} horizontal>
        <Button onClick={() => onDismiss()} variant="secondary">
          Dismiss
        </Button>

        {isSignInError && (
          <Button onClick={() => sessionApi.signIn()} variant="primary">
            Sign In
          </Button>
        )}
      </Stack>
    </GlobalModal>
  );
};

const SandboxForkLimit: React.FC<{
  onDismiss: () => void;
  workspaceIdFromFork: string | null;
}> = ({ onDismiss, workspaceIdFromFork }) => {
  const editor = useSandboxEditor();
  const [fork, dispatchFork] = useForkSandbox();
  const activeWorkspace = useActiveWorkspace();
  const { workspaces, primaryWorkspaceId } = useUserWorkspaces();
  const currentUser = useCurrentUser();

  const [currentWorkspaceId, setCurrentWorkspaceId] = useState(
    workspaceIdFromFork ?? activeWorkspace?.id ?? primaryWorkspaceId,
  );
  const [currentCollectionPath, setCurrentCollectionPath] = useState("/");
  const [visibility, setVisibility] = useState<VisibilityNumber>(
    editor.privacy,
  );

  const currentWorkspace = workspaces.find(
    (item) => item.id === currentWorkspaceId,
  );

  if (!currentWorkspace) {
    return;
  }

  /**
   * Workspace sandbox limits
   */
  const shouldApplySandboxLimits =
    currentWorkspace.featureFlags.ubbBeta &&
    !currentWorkspace.featureFlags.friendOfCsb;
  const hasReachedPrivateLimit =
    shouldApplySandboxLimits &&
    currentWorkspace.usage.privateSandboxesQuantity >=
      currentWorkspace.limits.includedPrivateSandboxes;

  const handleSubmit = () => {
    if (!currentWorkspaceId) return;

    const collectionPath: string | undefined = currentCollectionPath;

    dispatchFork({
      type: "CREATE_FORK",
      workspaceId: currentWorkspaceId,
      collectionId: currentWorkspace.collections.find(
        (collection) => collection.path === collectionPath,
      )?.id,
      privacy: visibility,
    });
  };

  if (!currentWorkspaceId) return;

  /**
   * UI conts
   */
  const isTeamAdmin = getIsTeamAdmin(currentUser?.id ?? "", currentWorkspace);
  const disabledFork =
    (visibility === 2 && hasReachedPrivateLimit) || !currentWorkspaceId;
  const loading = fork.state === "FORKING";
  const label = editor.isCloud ? "Devbox" : "Sandbox";
  const pluralLabel = editor.isCloud ? "Devboxes" : "Sandboxes";

  return (
    <GlobalModal maxWidth={500} onOpenChange={onDismiss} open>
      <Box
        css={{
          position: "absolute",
          right: "$4",
          top: "$4",
          zIndex: 200,
        }}
      >
        <Dialog.CloseIconButton />
      </Box>

      <Dialog.Title>
        <Stack horizontal>
          <Icon size={4} type="circleBang" />

          <span>Failed to fork {label}</span>
        </Stack>
      </Dialog.Title>

      <Dialog.Description>
        You reached the maximum amount of private {pluralLabel} in this
        workspace. Choose another folder to fork to or{" "}
        {isTeamAdmin ? (
          <a
            href={paths.upgrade({
              workspaceId: currentWorkspaceId,
              source: "sandbox-limit",
            })}
            rel="noreferrer"
            target="_blank"
          >
            Upgrade to Pro
          </a>
        ) : (
          "Contact team admin"
        )}{" "}
        for unlimited {pluralLabel}.
      </Dialog.Description>

      <Stack gap={6}>
        <Stack>
          <Text>Workspace</Text>
          <Select
            defaultValue={currentWorkspaceId}
            onValueChange={(value) => {
              setCurrentWorkspaceId(value);
            }}
            variant="solid"
          >
            {workspaces.map((workspace) => {
              return (
                <Select.Item key={workspace.id} value={workspace.id}>
                  <Stack horizontal>
                    <Avatar
                      name={workspace.name}
                      size="s"
                      src={workspace.avatarUrl}
                    />
                    <Text> {workspace.name}</Text>

                    {getIsPro(workspace) ? (
                      <Badge variant="accentPrimary">Pro</Badge>
                    ) : null}
                  </Stack>
                </Select.Item>
              );
            })}
          </Select>
        </Stack>

        <Stack>
          <Text>Visibility</Text>
          <SandboxVisibilityUI
            onVisibilityChange={(privacy) => {
              setVisibility(visibilityToNumber(privacy));
            }}
            sandboxVisibility={numberToVisibility(visibility)}
            workspaceId={currentWorkspaceId}
          />
        </Stack>

        <Stack>
          <Text>Folder</Text>

          <Select
            key={currentWorkspaceId + currentCollectionPath}
            defaultValue={currentCollectionPath}
            onValueChange={(value) => {
              setCurrentCollectionPath(value);
            }}
            variant="solid"
          >
            {currentWorkspace.collections.map((collection) => {
              const path =
                collection.path === "/"
                  ? "All folders"
                  : collection.path.split("/").join(" / ");

              return (
                <Select.Item key={collection.id} value={collection.path}>
                  <Stack horizontal>
                    <Icon size={4} type="folderOpen" />
                    <Text>{path}</Text>
                  </Stack>
                </Select.Item>
              );
            })}
          </Select>
        </Stack>

        <Stack css={{ justifyContent: "flex-end" }} horizontal>
          <Dialog.Close onClick={() => onDismiss()}>
            <Button>Cancel</Button>
          </Dialog.Close>

          <Button
            disabled={disabledFork}
            loading={loading}
            onClick={handleSubmit}
            variant="primary"
          >
            Fork {label}
          </Button>
        </Stack>
      </Stack>
    </GlobalModal>
  );
};
